'use client';

import React from 'react';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Don't store the error object directly - it may contain non-serializable data
    // We'll extract serializable info in componentDidCatch
    return {
      hasError: true,
      error: null, // Will be set in componentDidCatch with serializable data
    };
  }

  componentDidCatch(error, errorInfo) {
    // Create serializable error object (avoid Firestore Timestamps and other non-serializable data)
    const serializableError = {
      message: error?.message || 'Unknown error',
      name: error?.name || 'Error',
      stack: error?.stack || null,
    };

    // Create serializable errorInfo (only include componentStack, not full errorInfo object)
    const serializableErrorInfo = {
      componentStack: errorInfo?.componentStack || null,
    };

    // Update state with serializable error details
    this.setState({
      error: serializableError,
      errorInfo: serializableErrorInfo,
    });

    // Basic error logging only - no PostHog tracking

    // Call custom error handler if provided
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (handlerError) {
        console.error('Error in custom error handler:', handlerError);
      }
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error?.message, errorInfo?.componentStack);
    }
  }

  // PostHog error tracking removed to prevent regex errors

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        try {
          const FallbackComponent = this.props.fallback;
          // Create a plain error object to avoid serialization issues
          const plainError = this.state.error ? {
            message: this.state.error.message || 'Unknown error',
            name: this.state.error.name || 'Error',
            stack: this.state.error.stack || null,
          } : null;
          return <FallbackComponent error={plainError} resetError={this.resetError} />;
        } catch (fallbackError) {
          console.error('Error rendering custom fallback:', fallbackError);
          // Fall through to default fallback
        }
      }

      // Default fallback UI - uses hardcoded English to avoid context dependencies
      // Create a plain error object to avoid serialization issues
      const plainError = this.state.error ? {
        message: this.state.error.message || 'Unknown error',
        name: this.state.error.name || 'Error',
        stack: this.state.error.stack || null,
      } : null;

      return (
        <DefaultErrorFallback
          error={plainError}
          resetError={this.resetError}
          level={this.props.level}
          featureName={this.props.featureName}
        />
      );
    }

    return this.props.children;
  }
}

// Default error fallback component - uses hardcoded English text
// This avoids dependency on NextIntlClientProvider which may not be available at root layout level
function DefaultErrorFallback({ error, resetError, level, featureName }) {
  // Hardcoded English translations (works even when NextIntlClientProvider is not available)
  const translations = {
    title: 'Something went wrong',
    message: "We encountered an unexpected error. Don't worry, your journey is still safe. Please try again or return home.",
    details: 'Error Details (Development Only)',
    tryAgain: 'Try Again',
    goHome: 'Go Home',
  };

  // Basic error logging only - no PostHog tracking

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 bg-[#050316] text-[#FBFBFE]">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-[#6A3683]/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-[#A45DE8]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h2 className="text-2xl font-marcellus text-[#FBFBFE]">
            {translations.title}
          </h2>
          <p className="text-[#D4B6FF] text-sm">
            {translations.message}
          </p>
          {process.env.NODE_ENV === 'development' && error && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-[#A45DE8] text-sm mb-2">
                {translations.details}
              </summary>
              <pre className="bg-[#050316] border border-[#6A3683]/40 rounded p-4 text-xs text-[#D4B6FF] overflow-auto max-h-48">
                {error.toString()}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={resetError}
            className="px-6 py-3 bg-[#A45DE8] hover:bg-[#8B4DD6] text-white rounded-lg font-medium transition-colors duration-200"
          >
            {translations.tryAgain}
          </button>
          <button
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = '/';
              }
            }}
            className="px-6 py-3 bg-[#6A3683]/40 hover:bg-[#6A3683]/60 text-[#FBFBFE] rounded-lg font-medium transition-colors duration-200"
          >
            {translations.goHome}
          </button>
        </div>
      </div>
    </div>
  );
}

// Wrapper component
export default function ErrorBoundary({ children, fallback, onError, level = 'component', featureName }) {
  return (
    <ErrorBoundaryClass fallback={fallback} onError={onError} level={level} featureName={featureName}>
      {children}
    </ErrorBoundaryClass>
  );
}
